/**
 * @dynamic resolves static lambda issue with package build
 * https://github.com/ng-packagr/ng-packagr/issues/696
 */

import { NON_MSO_LIST_CSS_PROPERTIES } from '../quill.const';

// @dynamic

/**
 * Contains functions to translate text copied from Microsoft Office desktop that contians one
 * or more lists into usable html.
 */
export class MSOListHelper {
    public static readonly MSO_UNORDERD_LIST_FONT_FAMILY = 'Symbol';
    public static readonly LIST_ITEM_TAG_NAME = 'LI';
    public static readonly ORDERED_LIST_TAG_NAME = 'OL';
    public static readonly UNORDERED_LIST_TAG_NAME = 'UL';
    public static readonly SPAN_TAG_NAME = 'SPAN';
    public static readonly ITALIC_TAG_NAME = 'I';
    public static readonly BOLD_TAG_NAME = 'B';

    public static readonly ORDERED_LIST_ITEM_REGEX = /[0-9]|[A-Z]|[a-z][\.|)]/g;

    public static readonly indentClassKeyToClassMap: Map<number, string> = new Map([
        [1, 'ql-indent-1'],
        [2, 'ql-indent-2'],
        [3, 'ql-indent-3'],
        [4, 'ql-indent-4'],
        [5, 'ql-indent-5'],
        [6, 'ql-indent-6'],
        [7, 'ql-indent-7'],
        [8, 'ql-indent-8'],
    ]);

    public static readonly msoIndentLevelToIndentClassKeyMap: Map<number, number> = new Map([
        [0, 0],
        [1, 1],
        [1.5, 2],
        [2, 3],
        [2.5, 4],
        [3, 5],
        [3.5, 6],
        [4, 7],
        [4.5, 8]
    ]);

    /**
     * MSO lists do not use html list tags. Transforms MSO lists within provided element to usable html list
     */
    public static transformMSOListToHTMLList(containerNode: HTMLElement): void {
        if (containerNode.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const elementList: HTMLElement[] = <HTMLElement[]>Array.from(containerNode.children);
        const newElementsMap: Map<number, HTMLElement>  = new Map();
        const nodesToBeReplacedMap: Map<number, HTMLElement>  = new Map();
        const msoListFound = elementList.find(element => this.isMSOListItem(element));
        let existingListInstanceNumber: number = -1;
        let danglingListIndex: number = -2;
        let shouldCreateNewList: boolean = true;

        if (!msoListFound) {
            return;
        }

        for (let index = 0; index < elementList.length; index++) {
            const element: HTMLElement = elementList[index];

            const listIndex = this.findListIdentifierIndex(element);
            if (listIndex === -1) {
                shouldCreateNewList = true;
                continue;
            }

            const currentListItemInfo = element.getAttribute('style')?.slice(listIndex);
            if (!currentListItemInfo) {
                shouldCreateNewList = true;
                continue;
            }
            // the index of current list
            const currentListInstanceNumber = parseInt(currentListItemInfo.slice(10), 10);
            if (isNaN(currentListInstanceNumber)) {
                shouldCreateNewList = true;
                continue;
            }
            shouldCreateNewList = shouldCreateNewList || currentListInstanceNumber !== existingListInstanceNumber;

            if (shouldCreateNewList) {
                const newList: HTMLOListElement | HTMLUListElement = MSOListHelper.createNewListFromMSOFirstChild(element);
                const listItem: HTMLElement = MSOListHelper.convertMSOListItemToHTMLElement(element);
                newList.appendChild(listItem);
                if (newElementsMap.has(currentListInstanceNumber)) {
                    // Word accepts lists broken by new non-nested lists or new linesy, but stand HTML does not allow it,
                    // so when encountering a new list with the same index as before, we need to store the old list with a different key in the map
                    const savedNewElement = <HTMLElement>newElementsMap.get(currentListInstanceNumber);
                    const savedReplacedElement = <HTMLElement>nodesToBeReplacedMap.get(currentListInstanceNumber);
                    newElementsMap.set(danglingListIndex, savedNewElement);
                    nodesToBeReplacedMap.set(danglingListIndex, savedReplacedElement);
                    danglingListIndex--;
                }
                newElementsMap.set(currentListInstanceNumber, newList);
                nodesToBeReplacedMap.set(currentListInstanceNumber, element);
                existingListInstanceNumber = currentListInstanceNumber;
            } else {
                const newList: HTMLElement = newElementsMap.get(currentListInstanceNumber);
                const newElement: HTMLElement = MSOListHelper.convertMSOListItemToHTMLElement(element);
                newList.appendChild(newElement);
                newElementsMap.set(currentListInstanceNumber, newList);
                element.remove();
            }
            shouldCreateNewList = false;
        }

        for (const [listNumber, newListElement] of newElementsMap.entries()) {
            const nodeToBeReplaced: HTMLElement = nodesToBeReplacedMap.get(listNumber);
            nodeToBeReplaced.replaceWith(newListElement);
        }
    };

    public static convertMSOListItemToHTMLElement(msoListItemNode: HTMLElement): HTMLElement {
        const newLineBreakIndex = msoListItemNode.innerText?.indexOf('\n');
        if (!newLineBreakIndex) {
            console.log('should not get here');
            return msoListItemNode;
        }
        const listItemTextContent = msoListItemNode.innerText.substring(newLineBreakIndex + '\n'.length);
        const listItem = document.createElement('LI');

        MSOListHelper.assignStylesToNode(listItem, msoListItemNode);
        const indentLevelClass = MSOListHelper.getIndentClass(msoListItemNode);
        if (indentLevelClass) {
            listItem.classList.add(indentLevelClass);
        }

        msoListItemNode.classList.forEach(className => {
            listItem.classList.add(className);
        });

        const innerHTML: Document = new DOMParser().parseFromString(msoListItemNode.innerHTML, 'text/html');
        const body: HTMLBodyElement = innerHTML.querySelector('body');

        if (!listItemTextContent.trim() || !body.childNodes.length) {
            return listItem;
        }

        let listContentChildNodes = body.childNodes;
        // When selecting all from Word, some list content is adjusted for bookmark
        if (body.children.length <= 2 &&
            (body.children[0].tagName === 'A' || body.children[0].getAttribute('style')?.indexOf('mso-bookmark') > -1)
        ) {
            listContentChildNodes = body.children[0].childNodes;
        }

        if (listContentChildNodes.length < 3) {
            listItem.appendChild(listContentChildNodes[0]);

            return listItem;
        } else {
            while (listContentChildNodes.length >= 3) {
                // appendChild removes the child from parent, so no need to increase index
                listItem.appendChild(listContentChildNodes[2]);
            }

            return listItem;
        }
    };

    /**
     * Walks children of the provided element until it finds a SPAN element
     */
    public static findFirstSpanChild(element: HTMLElement): HTMLElement {
        let spanElements = Array.from(element.childNodes).map(childElement => {
            if (childElement.nodeName === MSOListHelper.SPAN_TAG_NAME) {
                return childElement;
            }

            if (childElement.childNodes.length) {
                return MSOListHelper.findFirstSpanChild(<HTMLElement>childElement);
            }

            return null;
        });

        spanElements = spanElements.filter(spanElement => spanElement !== null);

        if (spanElements && spanElements.length) {
            return <HTMLElement>spanElements[0];
        }

        return null;
    }

    /**
     * Returns a boolean indicating if the provided element is an ordered list
     */
    public static isListOrdered(element: HTMLElement): boolean {
        const firstSpanElement: HTMLElement = MSOListHelper.findFirstSpanChild(element);

        if (!firstSpanElement) {
            return false;
        }

        const listMarkerContainerChildren = Array.from(firstSpanElement.childNodes).filter(childElement => {
            if (childElement.nodeName === MSOListHelper.SPAN_TAG_NAME) {
                return true;
            }

            return false;
        });

        if (!listMarkerContainerChildren.length) {
            return false;
        }

        const listMarkerElement = listMarkerContainerChildren[0];
        const orderedListMatchFound = listMarkerElement.textContent.match(MSOListHelper.ORDERED_LIST_ITEM_REGEX);
        if (orderedListMatchFound) {
            return true;
        }

        return false;
    }

    /**
     * Returns true if the provided mso list item has a bullet or number before its list item content
     */
    public static msoListItemHasMarkerBeforeContent(msoListItem: HTMLElement): boolean {
        const childNodes = Array.from(msoListItem.childNodes);
        const listItemElementChildren = childNodes.filter(childNode => childNode.nodeType === Node.ELEMENT_NODE);

        return listItemElementChildren.length === 2;
    };

    /**
     * Returns a new UL or OL element based on the list item provided.
     */
    public static createNewListFromMSOFirstChild(msoListElement: HTMLElement): HTMLOListElement | HTMLUListElement {
        const isOrderedList = MSOListHelper.isListOrdered(msoListElement);

        if (isOrderedList) {
            return <HTMLOListElement>document.createElement('OL');
        }

        return <HTMLUListElement>document.createElement('UL');
    };

    /**
     * Assigns styles to the destination node from the source node
     */
    public static assignStylesToNode(destinationNode: HTMLElement, sourceNode: HTMLElement): void {
        const nodeStyles = MSOListHelper.getDefinedStylesFromNode(sourceNode);
        for (const key in nodeStyles) {
            if (nodeStyles[key] === undefined || nodeStyles[key] === null || NON_MSO_LIST_CSS_PROPERTIES.includes(key)) {
                continue;
            }

            destinationNode.style[key] = nodeStyles[key];
        }
    };

    /**
     * Returns the quill class for the indent level
     **/
    public static getIndentClass(node: HTMLElement): string {
        const INDENT_LEVEL_MARKER: string = 'level';

        const styleAttribute = node.getAttribute('style');
        if (!styleAttribute) {
            return '';
        }
        const msoListStartIndex = styleAttribute.indexOf('mso-list');
        const msoListEndIndex = styleAttribute.indexOf('lf', msoListStartIndex);

        const msoValue = styleAttribute.substring(msoListStartIndex, msoListEndIndex);
        if (!msoValue) {
            return '';
        }
        const indentLevelStartIndex = msoValue.indexOf(INDENT_LEVEL_MARKER) + INDENT_LEVEL_MARKER.length;
        let indentLevel: number = +msoValue.substring(indentLevelStartIndex, indentLevelStartIndex + 1);

        if (isNaN(indentLevel)) {
            return '';
        }

        if (indentLevel > 0) {
            indentLevel = indentLevel - 1;
        }

        return `ql-indent-${indentLevel}`;
    }

    /**
     * Returns the styles defined on the provided html element
     */
    public static getDefinedStylesFromNode(node: HTMLElement): any {
        const nodeStyle: CSSStyleDeclaration = node.style;
        // CSSStyleDeclaration has numeric keys whose values are the keys for which styles are defined

        const styleKeysWithDefinedValues = Object.keys(nodeStyle)
            .filter(key => !isNaN(+key))
            .map(styleKeyIndex => nodeStyle[styleKeyIndex]);

        const style = {};
        for (const key of styleKeysWithDefinedValues) {
            style[key] = node.style[key];
        }

        return style;
    };

    public static isMSOListItem(element: HTMLElement): boolean {
        return this.findListIdentifierIndex(element) > -1;
    }

    private static findListIdentifierIndex(element: HTMLElement): number {
        return element.getAttribute('style')?.indexOf('mso-list');
    }
}
